import React from 'react';
import { type AggregatedMetrics } from '@prerender/prerender-shared/FE';
import { Bar } from 'react-chartjs-2';

import { getXAxisConfig } from '~features/dashboard-v3/charts';
import { MainChartLegend, ChartCard } from '~components/Charts';
import { Colors } from '~library/prerenderTheme';
import { numberFormatter } from '~library/numbers';
import ChartContainer, { type CDNAnalyticsChartContainerProps } from '../CDNAnalyticsChartContainer';
import buildResponseTimeDatasets from './buildResponseTimeDatasets';
import { useChartItemsVisibility, type ChartConfig } from '~library/chartjs';
import useTrackCDNAnalyticsEvent from '../useTrackCDNAnalyticsEvent';
import { useIsPrinting } from '~library/print';

export const CHART_ID = 'cdn-analytics-response-time-chart';
export const RESPONSE_TIME_VISIBILITY_CHARTS: ChartConfig[] = [{ id: CHART_ID, type: 'dataset' }];
export const TOOLTIP_MESSAGE =
  'Time to serve pages to crawlers. Hits are served from cache; misses include rendering time.';

function CDNAnalyticsResponseTimeChart({ rawData }: { rawData: AggregatedMetrics.CacheHitMissTimeResult }) {
  const track = useTrackCDNAnalyticsEvent();
  const ChartItemsVisibility = useChartItemsVisibility(RESPONSE_TIME_VISIBILITY_CHARTS);
  const { isPrinting } = useIsPrinting();

  const datasets = React.useMemo(() => buildResponseTimeDatasets(rawData), [rawData]);

  const onLegendItemClicked = (datasetIndex: number) => {
    track('ChartDatasetToggled', {
      chart: 'crawler visits - response time',
      dataset: datasets[datasetIndex].label || 'n/a',
      action: ChartItemsVisibility.itemsVisibility[datasetIndex] ? 'hide' : 'show',
    });
    ChartItemsVisibility.toggleVisibility(datasetIndex);
  };

  React.useEffect(() => {
    ChartItemsVisibility.restoreVisibility(datasets.length);
  }, [datasets]);

  return (
    <ChartCard.Content>
      <MainChartLegend>
        <MainChartLegend.Label
          label="Cache hits"
          labelColor={Colors.Mint}
          value={`${numberFormatter.format(rawData.cache_hit_time_avg / 1000)}s`}
          crossedOut={!ChartItemsVisibility.itemsVisibility[0]}
          onClick={() => onLegendItemClicked(0)}
        />
        <MainChartLegend.Label
          label="Cache misses"
          labelColor={Colors.Gold}
          value={`${numberFormatter.format(rawData.cache_miss_time_avg / 1000)}s`}
          crossedOut={!ChartItemsVisibility.itemsVisibility[1]}
          onClick={() => onLegendItemClicked(1)}
        />
      </MainChartLegend>

      <ChartCard.ChartLayout>
        <Bar
          id={CHART_ID}
          data={{ labels: rawData.intervals, datasets }}
          options={{
            animation: isPrinting ? false : undefined,
            scales: {
              x: getXAxisConfig(rawData.granularity),
              y: {
                type: 'linear',
                ticks: {
                  count: 5,
                  callback: (value) => `${numberFormatter.format(Number(value) / 1000)}s`,
                },
              },
            },
            plugins: {
              tooltip: {
                callbacks: {
                  label: (context) =>
                    `${context.dataset.label}: ${numberFormatter.format(Number(context.parsed.y) / 1000)}s`,
                },
              },
            },
          }}
        />
      </ChartCard.ChartLayout>
    </ChartCard.Content>
  );
}

const MemoizedCDNAnalyticsResponseTimeChart = React.memo(CDNAnalyticsResponseTimeChart);

type ContainerProps = CDNAnalyticsChartContainerProps & {
  rawData?: AggregatedMetrics.CacheHitMissTimeResult;
};
function Container({ isLoading, rawData, ...props }: ContainerProps) {
  const hasDataForTimeRange = !!rawData && !!rawData.data.length;

  return (
    <ChartContainer {...props} hasDataForTimeRange={hasDataForTimeRange} isLoading={isLoading}>
      {rawData && <MemoizedCDNAnalyticsResponseTimeChart rawData={rawData} />}
    </ChartContainer>
  );
}

export default Container;
